import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Heart, Coffee, Wrench, BabyCarriage } from "@phosphor-icons/react";

const FATHER_WEEKLY = {
  1: "She may not know yet. Be patient with energy dips and quiet emotions.",
  4: "She's testing. Be there for the moment. Buy the test together if you can.",
  8: "Morning sickness is real. Bring crackers, water, presence — not solutions.",
  12: "The first scan. Plan to be there. The heartbeat is something you'll never forget.",
  16: "Start talking to the bump. Babies hear voices now. Yours included.",
  20: "Anomaly scan week. Big appointment. Cancel everything else.",
  24: "Help her track baby movements. Foot massages help round-ligament pain.",
  28: "Third trimester. Take on more household tasks. She'll thank you forever.",
  32: "Start hospital bag prep together. Practice the route at her pace.",
  36: "Sleep is fading. Pick up nights when you can. Listen more than you speak.",
  40: "Phones charged. Bag at door. Take a breath. You're a father any moment now.",
};

const FATHER_TIPS = [
  { icon: Heart, title: "Emotional support", body: "She doesn't need fixing. She needs witnessing. Repeat what she says. Hold her hand. Stay." },
  { icon: Coffee, title: "Practical care", body: "Take groceries off her plate. Cook simple meals. Drive her to appointments. Refill her water." },
  { icon: Wrench, title: "Home preparation", body: "Install the car seat (badly the first time). Stock the nursery. Childproof slowly. It takes longer than you think." },
  { icon: BabyCarriage, title: "Bond with baby now", body: "Talk to the bump. Read aloud. Sing. Babies recognize voices at birth. You're already a father." },
];

export default function FatherMode() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);

  const week = data?.pregnancy_status?.current_week || 0;
  const closest = Object.keys(FATHER_WEEKLY).map(Number).reduce((a, b) => (Math.abs(b - week) < Math.abs(a - week) ? b : a), 1);
  const guidance = FATHER_WEEKLY[closest];

  return (
    <div className="space-y-10" data-testid="father-mode-page">
      <header>
        <p className="overline text-saffron mb-2">Father mode</p>
        <h1 className="font-serif font-semibold text-4xl sm:text-5xl">You're becoming a father.</h1>
        <p className="text-ink-700 mt-2 max-w-2xl">A separate journey. Equally sacred. Here's what to do, how to help, and how to grow into this.</p>
      </header>

      {week > 0 && (
        <div className="vansha-card p-8 sm:p-10 bg-forest text-white" data-testid="father-week-card">
          <p className="overline" style={{ color: "#D9C9B4" }}>This week (~{week})</p>
          <p className="font-serif text-3xl mt-3 leading-snug">{guidance}</p>
          <Link to="/app/pregnancy" className="text-saffron underline-offset-2 hover:underline text-sm mt-4 inline-block">See her week-by-week →</Link>
        </div>
      )}

      <section data-testid="father-pillars">
        <h2 className="font-serif text-3xl mb-6">Four ways to show up</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {FATHER_TIPS.map((t) => (
            <div key={t.title} className="vansha-card p-6" data-testid={`father-tip-${t.title.toLowerCase().replace(/\s/g, '-')}`}>
              <t.icon weight="thin" size={28} className="text-terracotta mb-3" />
              <h3 className="font-serif text-xl">{t.title}</h3>
              <p className="text-ink-700 mt-2">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="grid sm:grid-cols-2 gap-6">
        <Link to="/app/appointments" className="vansha-card p-6 hover:shadow-lg transition" data-testid="father-appts">
          <p className="overline text-saffron mb-2">Be there</p>
          <h3 className="font-serif text-2xl">Appointments</h3>
          <p className="text-ink-700 mt-2 text-sm">Show up to every scan you can. They're not for her alone.</p>
        </Link>
        <Link to="/app/letters" className="vansha-card p-6 hover:shadow-lg transition" data-testid="father-letters">
          <p className="overline text-saffron mb-2">Speak from the heart</p>
          <h3 className="font-serif text-2xl">Write a letter</h3>
          <p className="text-ink-700 mt-2 text-sm">A note to your child — for one day, when they're old enough to read it.</p>
        </Link>
        <Link to="/app/delivery-prep" className="vansha-card p-6 hover:shadow-lg transition" data-testid="father-prep">
          <p className="overline text-saffron mb-2">Be ready</p>
          <h3 className="font-serif text-2xl">Hospital readiness</h3>
          <p className="text-ink-700 mt-2 text-sm">Bag, plan, contacts, transport. Be the one who has it all sorted.</p>
        </Link>
        <Link to="/app/academy/k5" className="vansha-card p-6 hover:shadow-lg transition" data-testid="father-academy">
          <p className="overline text-saffron mb-2">Learn</p>
          <h3 className="font-serif text-2xl">Becoming a Father (course)</h3>
          <p className="text-ink-700 mt-2 text-sm">35 minutes. Could change your year.</p>
        </Link>
      </section>
    </div>
  );
}
